import { ActiveFilter } from '../useFilters';
import { cleanEmptyFilters, separateFilters } from './filterHelpers';
import { isSuggestedSection } from './evidenceSectionHelpers';

type TabSection = {
  dataSource?: string;
  filters?: ActiveFilter[];
  conditions?: Array<{
    field?: string;
    $eq?: unknown;
    $neq?: unknown;
    $isNull?: number;
  }>;
};

type IdeaTab = {
  label?: string;
  name?: string;
  sections?: TabSection[];
};

export type ResolvedSuggestedSectionFilters = {
  sectionFilters: ActiveFilter[];
  apiMergeEnabled: boolean;
  mergedActive: ActiveFilter[];
  mergedTrend: ActiveFilter[];
  apiMergedSectionFilterFieldPaths: string[];
  sectionFiltersSignature: string;
  hasEmptyFilterIntersection: boolean;
};

const CLIENT_ONLY_SECTION_FILTER_KEYS = new Set(['thisIdea', 'not_set', 'unassigned']);

const getSectionFilterFieldPath = (filter: ActiveFilter): string =>
  filter.dbFieldPath || filter.name || '';

const normalizeFilterSelectedForSignature = (selected: ActiveFilter['selected']) => {
  if (Array.isArray(selected)) {
    return [...selected].map(String).sort();
  }
  return selected;
};

export const normalizeSectionFilterForApi = (filter: ActiveFilter): ActiveFilter => ({
  ...filter,
  filterObject: filter.filterObject ?? 'evidence',
});

const isClientOnlySectionFilter = (filter: ActiveFilter): boolean => {
  if (filter.name === '_id') {
    return true;
  }
  const selected = filter.selected;
  if (!Array.isArray(selected)) {
    return false;
  }
  return selected.some((value) => CLIENT_ONLY_SECTION_FILTER_KEYS.has(String(value)));
};

const isSuggestedClientOnlySectionFilterField = (fieldPath: string): boolean =>
  fieldPath === 'match'
  || fieldPath.startsWith('match.')
  || fieldPath === 'ideaId'
  || fieldPath === 'gtmIdeaId';

export const isSuggestedApiMergeableSectionFilter = (filter: ActiveFilter): boolean => {
  if (isClientOnlySectionFilter(filter)) {
    return false;
  }

  const fieldPath = getSectionFilterFieldPath(filter);
  return !isSuggestedClientOnlySectionFilterField(fieldPath);
};

const convertLegacyConditionToFilter = (condition: TabSection['conditions'][number]): ActiveFilter | null => {
  const field = condition.field;
  if (!field) {
    return null;
  }

  if (field === 'match' && condition.$isNull !== undefined) {
    return null;
  }

  if (condition.$eq !== undefined) {
    return normalizeSectionFilterForApi({
      name: field,
      type: 'checkbox',
      dbFieldPath: field,
      selected: [String(condition.$eq)],
      operator: 'in',
    });
  }

  if (condition.$neq !== undefined && condition.$neq === null) {
    return normalizeSectionFilterForApi({
      name: field,
      type: 'checkbox',
      dbFieldPath: field,
      selected: ['set'],
      operator: 'in',
    });
  }

  if (condition.$isNull !== undefined) {
    return normalizeSectionFilterForApi({
      name: field,
      type: 'checkbox',
      dbFieldPath: field,
      selected: condition.$isNull === 0 ? ['set'] : ['not_set'],
      operator: 'in',
    });
  }

  return null;
};

export const extractSectionActiveFilters = (section: TabSection): ActiveFilter[] => {
  if (section.filters?.length) {
    return section.filters
      .map(normalizeSectionFilterForApi)
      .filter((filter) => !isClientOnlySectionFilter(filter));
  }

  if (!section.conditions?.length) {
    return [];
  }

  return section.conditions
    .map(convertLegacyConditionToFilter)
    .filter((filter): filter is ActiveFilter => filter != null);
};

export const extractSectionActiveFiltersForApi = (section: TabSection): ActiveFilter[] =>
  extractSectionActiveFilters(section).filter(isSuggestedApiMergeableSectionFilter);

export const getSuggestedSections = (tab: IdeaTab | undefined): TabSection[] =>
  (tab?.sections || []).filter((section) => isSuggestedSection(section));

const intersectSelectedValues = (
  businessSelected: ActiveFilter['selected'],
  sectionSelected: ActiveFilter['selected']
): { selected: ActiveFilter['selected']; hasEmptyIntersection: boolean } => {
  const toArray = (value: ActiveFilter['selected']): string[] => {
    if (Array.isArray(value)) {
      return value.map(String);
    }
    if (value == null || value === '') {
      return [];
    }
    return [String(value)];
  };

  const businessValues = toArray(businessSelected);
  const sectionValues = toArray(sectionSelected);

  if (sectionValues.length === 0) {
    return { selected: businessSelected, hasEmptyIntersection: false };
  }
  if (businessValues.length === 0) {
    return { selected: sectionSelected, hasEmptyIntersection: false };
  }

  const intersection = businessValues.filter((value) => sectionValues.includes(value));
  return {
    selected: intersection,
    hasEmptyIntersection: intersection.length === 0,
  };
};

const mergeFilterWithSectionFilter = (
  businessFilter: ActiveFilter | undefined,
  sectionFilter: ActiveFilter
): { filter: ActiveFilter; hasEmptyIntersection: boolean } => {
  if (!businessFilter) {
    return { filter: sectionFilter, hasEmptyIntersection: false };
  }

  const { selected, hasEmptyIntersection } = intersectSelectedValues(
    businessFilter.selected,
    sectionFilter.selected
  );

  return {
    filter: {
      ...businessFilter,
      ...sectionFilter,
      filterObject: sectionFilter.filterObject ?? businessFilter.filterObject ?? 'evidence',
      selected,
    },
    hasEmptyIntersection,
  };
};

export const mergeBusinessFiltersWithSectionFilters = (
  businessFilters: ActiveFilter[],
  trendFilters: ActiveFilter[],
  sectionFilters: ActiveFilter[]
): {
  mergedActive: ActiveFilter[];
  mergedTrend: ActiveFilter[];
  hasEmptyFilterIntersection: boolean;
} => {
  const cleanBusinessFilters = cleanEmptyFilters(businessFilters);
  const cleanTrendFilters = cleanEmptyFilters(trendFilters);
  const cleanSectionFilters = cleanEmptyFilters(sectionFilters);

  if (cleanSectionFilters.length === 0) {
    return {
      mergedActive: cleanBusinessFilters,
      mergedTrend: cleanTrendFilters,
      hasEmptyFilterIntersection: false,
    };
  }

  const combinedBusinessFilters = [...cleanBusinessFilters, ...cleanTrendFilters];
  const filterByFieldPath = new Map<string, ActiveFilter>();
  combinedBusinessFilters.forEach((filter) => {
    filterByFieldPath.set(getSectionFilterFieldPath(filter), filter);
  });

  let hasEmptyFilterIntersection = false;
  const mergedFilterMap = new Map<string, ActiveFilter>(filterByFieldPath);

  cleanSectionFilters.forEach((sectionFilter) => {
    const fieldPath = getSectionFilterFieldPath(sectionFilter);
    const existingFilter = mergedFilterMap.get(fieldPath);
    const { filter, hasEmptyIntersection } = mergeFilterWithSectionFilter(
      existingFilter,
      sectionFilter
    );

    if (hasEmptyIntersection) {
      hasEmptyFilterIntersection = true;
    }

    mergedFilterMap.set(fieldPath, filter);
  });

  const mergedCombined = cleanEmptyFilters(Array.from(mergedFilterMap.values()));
  const { trendFilters: mergedTrend, businessFilters: mergedActive } = separateFilters(mergedCombined);

  return {
    mergedActive,
    mergedTrend,
    hasEmptyFilterIntersection,
  };
};

export const buildSectionFiltersCacheSignature = (sectionFilters: ActiveFilter[]): string => {
  const cleanSectionFilters = cleanEmptyFilters(sectionFilters);
  if (cleanSectionFilters.length === 0) {
    return '';
  }

  return JSON.stringify(
    cleanSectionFilters
      .map((filter) => ({
        dbFieldPath: getSectionFilterFieldPath(filter),
        selected: normalizeFilterSelectedForSignature(filter.selected),
        operator: filter.operator,
        type: filter.type,
      }))
      .sort((a, b) => a.dbFieldPath.localeCompare(b.dbFieldPath))
  );
};

const findTabByActiveLabel = (tabs: IdeaTab[], activeTab: string | null | undefined): IdeaTab | undefined => {
  if (!activeTab) {
    return undefined;
  }
  return tabs.find((tab) => tab.label === activeTab || tab.name === activeTab);
};

export const resolveSuggestedApiSectionFilters = (
  tabs: IdeaTab[],
  activeTab: string | null | undefined,
  businessFilters: ActiveFilter[],
  trendFilters: ActiveFilter[]
): ResolvedSuggestedSectionFilters => {
  const activeTabConfig = findTabByActiveLabel(tabs, activeTab);
  const suggestedSections = getSuggestedSections(activeTabConfig);
  const apiMergeEnabled = suggestedSections.length === 1;
  const sectionFilters = apiMergeEnabled
    ? extractSectionActiveFiltersForApi(suggestedSections[0])
    : [];

  const { mergedActive, mergedTrend, hasEmptyFilterIntersection } = mergeBusinessFiltersWithSectionFilters(
    businessFilters,
    trendFilters,
    sectionFilters
  );

  return {
    sectionFilters,
    apiMergeEnabled,
    mergedActive,
    mergedTrend,
    apiMergedSectionFilterFieldPaths: sectionFilters.map(getSectionFilterFieldPath),
    sectionFiltersSignature: buildSectionFiltersCacheSignature(sectionFilters),
    hasEmptyFilterIntersection,
  };
};

export const areMatchOnlyClientSectionFilters = (filters: ActiveFilter[]): boolean =>
  filters.length > 0
  && filters.every((filter) => isSuggestedClientOnlySectionFilterField(getSectionFilterFieldPath(filter)));

export const getClientSectionFiltersToApply = (
  section: TabSection,
  apiMergeEnabled: boolean,
  apiMergedSectionFilterFieldPaths: string[]
): ActiveFilter[] => {
  const sectionFilters = section.filters?.length
    ? section.filters
    : extractSectionActiveFilters(section);

  if (!apiMergeEnabled || !isSuggestedSection(section) || apiMergedSectionFilterFieldPaths.length === 0) {
    return sectionFilters;
  }

  return sectionFilters.filter(
    (filter) => !apiMergedSectionFilterFieldPaths.includes(getSectionFilterFieldPath(filter))
  );
};
